import React, {
  MouseEventHandler,
  useState,
  useRef,
  useEffect,
  useCallback
} from "react";
import DOMPurify from "dompurify";
import { Paragraph } from "@tvg/design-system/web";
import { Icon } from "@tvg/design-system";
import { openExternalLink } from "@tvg/sh-utils/mobileUtils";
import {
  Container,
  PlayerWrapper,
  CloseButton,
  Description,
  Content,
  Footer
} from "./styled-components";
import JWPlayer, { Player } from "../../JWPlayer";
import { JwtTrack } from "../types";
import { JWPlayerAPITracks } from "../../../types";

interface VideoPlayerDetailsProps {
  src: string;
  library: string;
  title?: string;
  description?: string;
  uploadedAt?: string;
  tracks?: JwtTrack[];
  isAutoPlay?: boolean;
  renderCaptionsNatively?: boolean;
  allowFullscreen?: boolean;
  qaLabel?: string;
  onClose?: MouseEventHandler<HTMLButtonElement>;
  height?: number;
  width?: number;
  onPause?: () => void;
  onPlay?: (isViewable: boolean) => void;
  onVideoViewed?: () => void;
  onVideoPlayedCompletely?: () => void;
  onVideoPlayAttemptFailed?: (message: string) => void;
  onSetupError?: (message: string) => void;
  onVideoMuteAndUnmute?: (isMuted: boolean) => void;
  onVideoFullScreen?: (isFullscreen: boolean) => void;
  deviceLockRotationToggle: (isLock: boolean) => void;
  isModalOpen: boolean;
}

const VideoPlayerDetails = ({
  isModalOpen = false,
  src,
  library,
  title,
  description,
  uploadedAt,
  tracks,
  isAutoPlay = false,
  renderCaptionsNatively = false,
  allowFullscreen = false,
  qaLabel = "video-details",
  onClose,
  height = 468,
  width = 833,
  onPause,
  onPlay,
  onVideoViewed,
  onVideoPlayedCompletely,
  onVideoPlayAttemptFailed,
  onSetupError,
  onVideoMuteAndUnmute,
  onVideoFullScreen,
  deviceLockRotationToggle
}: VideoPlayerDetailsProps) => {
  // Temporary implementation: Most of the code in this component is based on tutorials.
  // The `isMobile` variable is currently as false, because the code under isMobile is not needed for the new desktop version.
  // This code is retained to facilitate future mobile support, making it easier to implement when needed.
  const isMobile = false;
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [videoPlayer, setVideoPlayer] = useState<Player>();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const attachEvents = (vPlayer: Player) => {
    vPlayer.on("pause", () => onPause && onPause());
    vPlayer.on("play", (e) => onPlay && onPlay(!!e.viewable));
    vPlayer.on(
      "complete",
      () => onVideoPlayedCompletely && onVideoPlayedCompletely()
    );
    vPlayer.on(
      "error",
      (e) => onVideoPlayAttemptFailed && onVideoPlayAttemptFailed(e.message)
    );
    vPlayer.on("setupError", (e) => onSetupError && onSetupError(e.message));
    vPlayer.on("ready", () => onVideoViewed && onVideoViewed());
    vPlayer.on(
      "mute",
      (mute) => onVideoMuteAndUnmute && onVideoMuteAndUnmute(!!mute.mute)
    );
    vPlayer.on("fullscreen", (fullscreen) => {
      setIsFullscreen(!!fullscreen.fullscreen);

      if (onVideoFullScreen) {
        onVideoFullScreen(!!fullscreen.fullscreen);
      }
    });
  };

  const detachEvents = (vPlayer: Player) => {
    vPlayer.off("pause", () => onPause && onPause());
    vPlayer.off("play", (e) => onPlay && onPlay(!!e.viewable));
    vPlayer.off(
      "complete",
      () => onVideoPlayedCompletely && onVideoPlayedCompletely()
    );
    vPlayer.off(
      "error",
      (e) => onVideoPlayAttemptFailed && onVideoPlayAttemptFailed(e.message)
    );
    vPlayer.off("setupError", (e) => onSetupError && onSetupError(e.message));
    vPlayer.off("ready", () => onVideoViewed && onVideoViewed());
    vPlayer.off(
      "mute",
      (mute) => onVideoMuteAndUnmute && onVideoMuteAndUnmute(!!mute)
    );
    vPlayer.off(
      "fullscreen",
      (fullscreen) => onVideoFullScreen && onVideoFullScreen(!!fullscreen)
    );
  };

  useEffect(() => {
    if (videoPlayer) {
      attachEvents(videoPlayer);
    }

    return () => {
      if (videoPlayer) detachEvents(videoPlayer);
    };
  }, [videoPlayer]);

  useEffect(() => {
    // Delay rendering the video until the modal's animation completes,
    // ensuring the control bar layout initializes correctly
    if (isModalOpen) {
      setTimeout(() => {
        setShowVideo(true);
      }, 400);
    }
  }, [isModalOpen]);

  const resizeVideoToWrapper = useCallback(() => {
    if (wrapperRef.current && videoPlayer) {
      const { width: wrapperWidth, height: wrapperHeight } =
        wrapperRef.current.getBoundingClientRect();

      videoPlayer.resize(wrapperWidth, wrapperHeight);
    }
  }, [wrapperRef, videoPlayer]);

  useEffect(() => {
    if (isMobile) {
      resizeVideoToWrapper();
    }
  }, [wrapperRef, videoPlayer, isFullscreen, isMobile]);

  const orientationChange = useCallback(() => {
    const isLandscape = !window.matchMedia("(orientation: portrait)").matches;
    if (videoPlayer?.getState() === "playing") {
      setIsFullscreen(isLandscape);
      videoPlayer?.setFullscreen(isLandscape);
    }
    resizeVideoToWrapper();
  }, [videoPlayer, setIsFullscreen, resizeVideoToWrapper]);

  useEffect(() => {
    if (isMobile) {
      if (window) {
        deviceLockRotationToggle(false);
        // Only to clean the listener if already exists
        window.removeEventListener("resize", orientationChange);
        window.addEventListener("resize", orientationChange);
      }

      return () => {
        window.removeEventListener("resize", orientationChange);
        deviceLockRotationToggle(true);
      };
    }
    return () => null;
  }, [videoPlayer]);

  const parseWithTarget = (desc: string) => {
    DOMPurify.addHook("afterSanitizeAttributes", (node) => {
      if ("target" in node) {
        node.setAttribute("id", node.getAttribute("href") || "");
        setTimeout(() => {
          const element = document.getElementById(
            node.getAttribute("href") || ""
          );
          if (element)
            element.addEventListener("click", (e) => {
              e.preventDefault();
              e.stopPropagation();
              openExternalLink(element.getAttribute("href") || "");
            });
        }, 50);
      }
    });

    return DOMPurify.sanitize(desc);
  };

  return (
    <Container data-qa-label={qaLabel}>
      <PlayerWrapper
        ref={wrapperRef}
        data-qa-label={`${qaLabel}-player-wrapper`}
        height={height}
      >
        {onClose && (
          <CloseButton data-qa-label={`${qaLabel}-close`} onClick={onClose}>
            <Icon name="close" size="m" lineColor="white.900" />
          </CloseButton>
        )}
        {showVideo && src && library && (
          <JWPlayer
            src={src}
            tracks={tracks as unknown as JWPlayerAPITracks[]}
            library={library}
            isAutoPlay={isAutoPlay}
            renderCaptionsNatively={renderCaptionsNatively}
            allowFullscreen={allowFullscreen}
            didMountCallback={({ player }) => setVideoPlayer(player)}
            height={height}
            width={width}
          />
        )}
      </PlayerWrapper>
      {!isFullscreen && (
        <Content>
          {description && (
            <Description
              data-qa-label={`${qaLabel}-description`}
              dangerouslySetInnerHTML={{
                __html: parseWithTarget(description)
              }}
            />
          )}
          <Footer>
            <Paragraph
              fontFamily="condensedRegular"
              fontSize="s"
              color="--fd-colors-content-subtle"
              textTransform="uppercase"
              qaLabel={`${qaLabel}-title`}
            >
              {title}
            </Paragraph>
            <Paragraph
              fontFamily="condensedRegular"
              fontSize="xs"
              textTransform="uppercase"
              color="--fd-colors-content-subtle"
              qaLabel={`${qaLabel}-timestamp`}
            >
              {uploadedAt}
            </Paragraph>
          </Footer>
        </Content>
      )}
    </Container>
  );
};

export default VideoPlayerDetails;
